if (!customElements.get("product-info")) {
  customElements.define(
    "product-info",
    class ProductInfo extends HTMLElement {
      abortController = undefined;
      onVariantChangeUnsubscriber = undefined;
      pendingRequestUrl = null;
      optionChangeRequestId = 0;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      cartUpdateUnsubscriber = undefined;
      htmlCacheForRequestUrl = new Map();
      mediaGalleryHtmlCache = new Map();
      mediaGalleryPrefetching = new Set();
      batchRequestCount = 5
      htmlCacheDuration = 5*60*1000
      htmlCacheRefreshLoopTimer = null
      abortHtmlCacheBatchRequest = undefined
      isHtmlCacheBatchRequestAborted = false

      constructor() {
        super();
      }

      get variantSelectors() {
        return this.querySelector("variant-selects");
      }

      get mediaGallery() {
        return this.querySelector("media-gallery");
      }

      get productId() {
        return this.getAttribute("data-product-id");
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get productForm() {
        return this.querySelector('form[is="product-form"]');
      }

      get quantityInput() {
        return this.querySelector("quantity-input input");
      }

      get isMobile() {
        return Boolean(FoxTheme.config?.mqlMobile)
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();

        this.applyShadeFromUrl();

        this.currentVariant = this.getSelectedVariant(this);
        if (this.currentVariant) {
          this.syncVariantSkuBadges(this.currentVariant);
          this.activeMediaGalleryKey = this.getMediaGalleryCacheKey(
            this.currentVariant
          );
          this.updateMedia(this.currentVariant);
        }

        this.bindMediaGalleryPrefetch();

        requestAnimationFrame(() => {
          if (this.canUseClientVariantUpdate()) {
            // Warm shade gallery HTML in idle time so first color click is cache-hit.
            this.scheduleIdleShadeGalleryPrefetch();
            return;
          }
          this.initHtmlCache();
          this.scheduleIdlePrefetch();
        });
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
        this.variantSelectors?.removeEventListener(
          "pointerenter",
          this.onMediaGalleryPrefetchPointerEnter
        );
        this.removeEventListener(
          "variant-tabs:change",
          this.onVariantTabsChange
        );
        clearTimeout(this.backgroundVariantSyncTimer);
        clearTimeout(this.mediaGallerySyncTimer);
        clearTimeout(this.htmlCacheRefreshLoopTimer);
        this.mediaGallerySyncAbort?.abort();
        this.abortHtmlCacheBatchRequest?.abort();
        if (this.idlePrefetchHandle && typeof cancelIdleCallback === "function") {
          cancelIdleCallback(this.idlePrefetchHandle);
        }
        if (
          this.idleShadePrefetchHandle &&
          typeof cancelIdleCallback === "function"
        ) {
          cancelIdleCallback(this.idleShadePrefetchHandle);
        }
      }

      scheduleIdlePrefetch() {
        const run = () => {
          this.getHtmlCacheForOptions().finally(() => {
            this.startRefreshHtmlCacheLoop();
          });
        };

        if (typeof requestIdleCallback === "function") {
          this.idlePrefetchHandle = requestIdleCallback(run, { timeout: 4000 });
        } else {
          setTimeout(run, 1500);
        }
      }

      getRequestOptionUrl(value) {
        let url = this.dataset.url ?? ''
        url += `?section_id=${this.sectionId}&option_values=${value}`
        return url
      }
      
      initHtmlCache() {
        const parentElement = this.parentElement
        if (!parentElement) return
        const selectedOptionValues = this.variantSelectors.selectedOptionValues ?? []
        const url = this.getRequestOptionUrl(selectedOptionValues?.join(','))
        this.htmlCacheForRequestUrl.set(url, {
          data: parentElement.outerHTML,
          cacheTime: Date.now()
        })
      }

      async getHtmlCacheForOptions() {
        if (this.canUseClientVariantUpdate()) return;

        const optionValueSets = this.variantSelectors?.prefetchOptionValueSets ?? [];
        if (!optionValueSets?.length) return;

        // Cap prefetch to avoid saturating network/main thread on large shade catalogs.
        const limitedSets = optionValueSets.slice(0, 6);
        let index = 0;
        const len = Math.ceil(limitedSets.length / this.batchRequestCount);
        this.abortHtmlCacheBatchRequest?.abort();
        this.abortHtmlCacheBatchRequest = new AbortController();
        this.isHtmlCacheBatchRequestAborted = false;

        while (index < len) {
          const requestOptions = limitedSets.slice(
            index * this.batchRequestCount,
            (index + 1) * this.batchRequestCount
          );
          const promises = [];
          requestOptions.forEach((optionValues) => {
            const requestUrl = this.getRequestOptionUrl(optionValues.join(","));
            const cacheData = this.htmlCacheForRequestUrl.get(requestUrl);
            if (!cacheData || Date.now() - cacheData.cacheTime > this.htmlCacheDuration) {
              promises.push(
                fetch(requestUrl, { signal: this.abortHtmlCacheBatchRequest.signal })
                  .then((response) => response.text())
                  .then((responseText) => {
                    this.htmlCacheForRequestUrl.set(requestUrl, {
                      data: responseText,
                      cacheTime: Date.now(),
                    });
                    return responseText;
                  })
                  .catch((error) => {
                    if (error.name === "AbortError") {
                      this.isHtmlCacheBatchRequestAborted = true;
                    } else {
                      console.error(error);
                    }
                  })
              );
            }
          });
          await Promise.allSettled(promises);
          index++;
        }
      }

      startRefreshHtmlCacheLoop() {
        if (this.canUseClientVariantUpdate()) return;
        if (this.htmlCacheRefreshLoopTimer) {
          clearTimeout(this.htmlCacheRefreshLoopTimer);
        }
        // Refresh much less often; large section HTML caches are expensive.
        this.htmlCacheRefreshLoopTimer = setTimeout(async () => {
          try {
            await this.getHtmlCacheForOptions();
          } finally {
            this.startRefreshHtmlCacheLoop();
          }
        }, 5 * 60 * 1000);
      }

      stopRefreshHtmlCacheLoop() {
        if (this.htmlCacheRefreshLoopTimer) {
          clearTimeout(this.htmlCacheRefreshLoopTimer)
        }
        this.abortHtmlCacheBatchRequest?.abort()
      }

      initializeProductSwapUtility() {
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({
        data: { event, target, selectedOptionValues },
      }) {
        if (!this.contains(event.target)) return;

        const productUrl =
          target.dataset.productUrl ||
          this.pendingRequestUrl ||
          this.dataset.url;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const viewMode = this.dataset.viewMode || "main-product";

        if (!shouldSwapProduct && this.tryClientVariantUpdate(productUrl, target, selectedOptionValues)) {
          return;
        }

        const shouldFetchFullPage =
          this.dataset.updateUrl === "true" && shouldSwapProduct;
        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(
            productUrl,
            selectedOptionValues,
            shouldFetchFullPage
          ),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage, viewMode)
            : this.handleUpdateProductInfo(productUrl, viewMode),
        });
      }

      getSelectedOptionValuesFromDom() {
        const optionNames = this.getProductOptionNames();
        if (!optionNames.length || !this.variantSelectors) return [];

        const radios = Array.from(
          this.variantSelectors.querySelectorAll('fieldset input[type="radio"]')
        );

        return optionNames.map((name, index) => {
          const position = index + 1;
          const checkedInput = radios.find(
            (input) =>
              input.checked && input.name.trim().endsWith(`-${position}`)
          );
          if (checkedInput) return checkedInput.value;

          const select = Array.from(this.variantSelectors.querySelectorAll("select")).find(
            (element) => element.name === `options[${name}]`
          );
          return select?.value ?? null;
        });
      }

      resolveVariantFromOptionValueIds(selectedOptionValues) {
        const productOptionValues = this.getProductOptionValues();
        const variants = this.getProductVariants();
        if (!productOptionValues.length || !variants.length || !selectedOptionValues?.length) {
          return null;
        }

        const idSet = new Set(selectedOptionValues.map(String));
        const selectedByPosition = productOptionValues
          .slice()
          .sort((left, right) => Number(left.position) - Number(right.position))
          .map((option) => {
            const match = option.values.find((value) => idSet.has(String(value.id)));
            return match?.name ?? null;
          });

        if (selectedByPosition.some((value) => !value)) return null;

        return (
          variants.find((variant) =>
            variant.options?.every(
              (optionValue, index) => optionValue === selectedByPosition[index]
            )
          ) ?? null
        );
      }

      resolveVariantFromCurrentSelection() {
        const variants = this.getProductVariants();
        const optionNames = this.getProductOptionNames();
        if (!variants.length || !optionNames.length) return null;

        const selectedValues = this.getSelectedOptionValuesFromDom();
        if (
          selectedValues.length !== optionNames.length ||
          selectedValues.some((value) => value == null)
        ) {
          return null;
        }

        return (
          variants.find((variant) =>
            variant.options?.every(
              (optionValue, index) => optionValue === selectedValues[index]
            )
          ) ?? null
        );
      }

      canUseClientVariantUpdate() {
        if (!this.variantSelectors || !this.getProductVariants().length) return false;
        if (this.dataset.clientVariantUpdates === "false") return false;
        if (this.querySelector(`#VolumeNote-${this.dataset.section}`)) return false;
        return true;
      }

      tryClientVariantUpdate(productUrl, target, selectedOptionValues) {
        if (!this.canUseClientVariantUpdate()) return false;

        const isPrimaryDependentChange = Boolean(
          this.variantSelectors?.dataset.dependentOptionValues === "true" &&
            (target?.closest?.(".product-form__input--dependent-primary") ||
              target?.classList?.contains("product-form__input--dependent-primary"))
        );

        let variant = null;
        if (isPrimaryDependentChange && selectedOptionValues?.length) {
          // PCS clicks already resolve the full compatible option set.
          variant = this.resolveVariantFromOptionValueIds(selectedOptionValues);
          if (variant) this.selectControlsForVariant(variant);
        } else {
          variant = this.resolveVariantFromCurrentSelection();
          if (!variant && selectedOptionValues?.length) {
            variant = this.resolveVariantFromOptionValueIds(selectedOptionValues);
            if (variant) this.selectControlsForVariant(variant);
          }
        }
        if (!variant) return false;

        this.optionChangeRequestId += 1;
        this.abortController?.abort();

        this.applyClientVariantUpdate(variant, productUrl);

        if (target?.id) {
          requestAnimationFrame(() => {
            document.getElementById(target.id)?.focus({ preventScroll: true });
          });
        }

        return true;
      }

      setSelectedVariantJson(variant) {
        const selectedVariantNode = this.variantSelectors?.querySelector(
          "[data-selected-variant]"
        );
        if (selectedVariantNode) {
          selectedVariantNode.textContent = JSON.stringify(variant);
        }
      }

      syncDependentOptionAvailability() {
        const variantSelects = this.variantSelectors;
        if (!variantSelects || variantSelects.dataset.dependentOptionValues !== "true") {
          return;
        }

        const variants = this.getProductVariants();
        const selectedValues = this.getSelectedOptionValuesFromDom();
        if (!variants.length || !selectedValues.length) return;

        const primaryValue = selectedValues[0];
        const matchingByShade = new Map();
        variants.forEach((variant) => {
          if (variant.options?.[0] !== primaryValue) return;
          const shade = variant.options?.[1];
          if (!shade) return;
          const entry = matchingByShade.get(shade) || { exists: true, available: false };
          entry.available = entry.available || Boolean(variant.available);
          matchingByShade.set(shade, entry);
        });

        variantSelects
          .querySelectorAll('fieldset input[type="radio"]')
          .forEach((input) => {
            if (!input.name.trim().endsWith("-2")) return;

            const match = matchingByShade.get(input.value);
            const exists = Boolean(match);
            const available = Boolean(match?.available);
            input.disabled = !exists;
            input.classList.toggle("disabled", exists && !available);
            input.classList.toggle("is-pcs-hidden", !exists);
          });

        variantSelects.querySelectorAll("variant-select-picker .option").forEach((option) => {
          const valueLabel = option.querySelector(".value-label")?.textContent?.trim();
          const match = matchingByShade.get(valueLabel);
          const exists = Boolean(match);
          const available = Boolean(match?.available);
          option.classList.toggle("is-pcs-hidden", !exists);
          option.classList.toggle("disabled", exists && !available);
        });

        this.syncDependentCategoryTabs(primaryValue);
      }

      syncDependentCategoryTabs(primaryValue) {
        const tabs = this.variantSelectors?.querySelector("variant-picker-tabs");
        if (!tabs) return;

        const normalized = String(primaryValue || "")
          .trim()
          .toLowerCase()
          .replace(/[\s-]/g, "");
        const showCategories = normalized === "1pcs" || normalized === "1pc";
        tabs.classList.toggle("is-pcs-categories-hidden", !showCategories);

        const forceFirstTab = tabs.dataset.forceFirstTab === "true";
        if (
          typeof tabs.applyActiveTab === "function" &&
          (!showCategories || forceFirstTab)
        ) {
          tabs.applyActiveTab(0);
        }
      }

      getVariantSkuTags() {
        if (this._variantSkuTags) return this._variantSkuTags;

        const json = this.variantSelectors?.querySelector(
          "[data-variant-sku-tags]"
        )?.textContent;
        if (!json) return null;

        try {
          this._variantSkuTags = JSON.parse(json).map((tag) => ({
            ...tag,
            skuList: new Set(
              String(tag.skus || "")
                .replace(/,/g, "\n")
                .split(/\r?\n/)
                .map((sku) => sku.replace(/<[^>]*>/g, "").trim())
                .filter(Boolean)
            ),
          }));
        } catch (error) {
          console.error(error);
          this._variantSkuTags = [];
        }
        return this._variantSkuTags;
      }

      syncVariantSkuBadges(selectedVariant) {
        const tags = this.getVariantSkuTags();
        if (!tags || !selectedVariant) return;

        const variants = this.getProductVariants();
        this.variantSelectors
          .querySelectorAll("label[data-sku-badge-option-position]")
          .forEach((label) => {
            if (label.querySelector(".hot-icon-wrapper--hot")) return;

            const input = this.variantSelectors.querySelector(
              `#${CSS.escape(label.htmlFor)}`
            );
            if (!input) return;

            const optionIndex = Number(label.dataset.skuBadgeOptionPosition) - 1;
            const variant = variants.find((candidate) =>
              candidate.options?.every((value, index) =>
                value === (index === optionIndex ? input.value : selectedVariant.options[index])
              )
            );
            const sku = String(variant?.sku || "").trim();
            const tag = sku && tags.find((entry) =>
              entry.skuList.has(sku) && (entry.icon || entry.label?.trim())
            );

            label.querySelectorAll(".hot-icon-wrapper").forEach((badge) => badge.remove());
            if (!tag) return;

            const position = ["left", "center", "right"].includes(tag.position)
              ? tag.position
              : "right";
            const badge = document.createElement("span");
            badge.className = `hot-icon-wrapper hot-icon-wrapper--${position}`;
            badge.setAttribute("aria-hidden", "true");

            if (tag.icon) {
              const width = Number(tag.width) > 0 ? Number(tag.width) : 20;
              badge.style.setProperty("--sku-tag-width", `${width}px`);
              const image = document.createElement("img");
              image.className = "hot-icon";
              image.src = tag.icon;
              image.alt = "";
              image.loading = "lazy";
              image.width = width;
              badge.append(image);
            } else {
              badge.classList.add("hot-icon-wrapper--label");
              badge.style.setProperty("--sku-tag-bg", tag.background || "");
              badge.style.setProperty("--sku-tag-color", tag.color || "");
              const text = document.createElement("span");
              text.className = "hot-icon-label";
              text.textContent = tag.label;
              badge.append(text);
            }
            label.append(badge);
          });
      }

      updatePriceFromVariant(variant) {
        const priceRoot =
          this.querySelector(`#price-${this.dataset.section} .f-price`) ||
          this.querySelector(`#price-${this.dataset.section}`);
        if (!priceRoot || !variant) return;

        const moneyFormat = FoxTheme.settings?.moneyFormat;
        const classes = {
          onSale: "f-price--on-sale",
          soldOut: "f-price--sold-out",
        };
        const salePrice = priceRoot.querySelector(".f-price-item--sale");
        const compareAtPrice = priceRoot.querySelectorAll(".f-price-item--regular");
        const unitPriceWrapper = priceRoot.querySelector(".f-price__unit-wrapper");
        const unitPrice = priceRoot.querySelector(".f-price__unit");
        const { compare_at_price: compareAtPriceValue, price, unit_price_measurement } = variant;

        priceRoot.classList.toggle(
          classes.onSale,
          Boolean(compareAtPriceValue && compareAtPriceValue > price)
        );
        priceRoot.classList.toggle(classes.soldOut, !variant.available);

        if (salePrice) {
          salePrice.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat);
        }

        compareAtPrice.forEach((item) => {
          item.innerHTML = FoxTheme.Currency.formatMoney(
            compareAtPriceValue && compareAtPriceValue > price
              ? compareAtPriceValue
              : price,
            moneyFormat
          );
        });

        if (unit_price_measurement && unitPrice && unitPriceWrapper) {
          unitPriceWrapper.classList.remove("hidden");
          unitPrice.innerHTML = `<span>${FoxTheme.Currency.formatMoney(
            variant.unit_price,
            moneyFormat
          )}</span>/<span data-unit-price-base-unit>${FoxTheme.Currency.getBaseUnit(
            variant
          )}</span>`;
        } else if (unitPriceWrapper) {
          unitPriceWrapper.classList.add("hidden");
        }
      }

      updateSkuFromVariant(variant) {
        const skuNode = this.querySelector(`#Sku-${this.dataset.section}`);
        if (!skuNode) return;

        const skuText = variant?.sku || "";
        skuNode.textContent = skuText;
        skuNode.classList.toggle("invisible", !skuText);
        skuNode.closest(".product__block--sku")?.classList.toggle("invisible", !skuText);
      }

      syncVariantSelectPickers(variant) {
        if (!variant || !this.variantSelectors) return;

        this.variantSelectors.querySelectorAll("variant-select-picker").forEach((picker) => {
          if (typeof picker.syncFromVariantSelects === "function") {
            picker.syncFromVariantSelects();
          }
        });
      }

      updateBadgesFromVariant(variant) {
        const badgesRoot = this.querySelector(`#Badges-${this.dataset.section}`);
        if (!badgesRoot || !variant) return;

        const available = Boolean(variant.available);
        const availableGroup = badgesRoot.querySelector("[data-badges-available]");
        const soldoutGroup = badgesRoot.querySelector("[data-badges-soldout]");

        if (availableGroup) {
          availableGroup.toggleAttribute("hidden", !available);
          availableGroup.classList.toggle("hidden", !available);
        }
        if (soldoutGroup) {
          soldoutGroup.toggleAttribute("hidden", available);
          soldoutGroup.classList.toggle("hidden", available);
        }

        if (!available) return;

        const saleBadge = badgesRoot.querySelector("[data-sale-badge]");
        if (!saleBadge) return;

        const price = Number(variant.price) || 0;
        const compareAtPrice = Number(variant.compare_at_price) || 0;
        const onSale = compareAtPrice > price;
        saleBadge.toggleAttribute("hidden", !onSale);
        saleBadge.classList.toggle("hidden", !onSale);
        if (!onSale) {
          saleBadge.textContent = "";
          return;
        }

        const saleType = saleBadge.dataset.saleType || "text";
        if (saleType === "percentage") {
          const amount = `${Math.floor(((compareAtPrice - price) * 100) / compareAtPrice)}%`;
          const template = saleBadge.dataset.saleTemplate || "Save __AMOUNT__";
          saleBadge.innerHTML = template.replace("__AMOUNT__", amount);
          return;
        }
        if (saleType === "fixed_amount") {
          const moneyFormat = FoxTheme.settings?.moneyFormat;
          const amount = FoxTheme.Currency.formatMoney(
            compareAtPrice - price,
            moneyFormat
          );
          const template = saleBadge.dataset.saleTemplate || "Save __AMOUNT__";
          saleBadge.innerHTML = template.replace("__AMOUNT__", amount);
          return;
        }

        saleBadge.textContent = saleBadge.dataset.saleText || "Sale";
      }

      applyClientVariantUpdate(variant, productUrl) {
        this.currentVariant = variant;
        this.setSelectedVariantJson(variant);
        this.syncDependentOptionAvailability();
        this.syncVariantSkuBadges(variant);
        this.syncVariantSelectPickers(variant);
        this.updatePriceFromVariant(variant);
        this.updateSkuFromVariant(variant);
        this.updateBadgesFromVariant(variant);
        this.updateURL(productUrl, variant.id, variant);
        this.updateShareUrl(variant.id, variant);
        this.updateVariantInputs(variant.id);
        this.pickupAvailability?.update(variant);
        this.toggleAddButton(!variant.available, FoxTheme.variantStrings.soldOut);
        this.syncMediaGalleryForVariant(productUrl, variant);

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
          data: {
            sectionId: this.sectionId,
            html: null,
            variant,
            source: "client",
          },
        });

        document.dispatchEvent(
          new CustomEvent("variant:changed", {
            detail: { variant },
          })
        );
      }

      syncMediaGalleryForVariant(productUrl, variant) {
        if (!variant?.id || this.isVariantMediaLocked()) return;

        const gallery = this.mediaGallery;
        const nextKey = this.getMediaGalleryCacheKey(variant);
        const mediaId = variant.featured_media?.id
          ? String(variant.featured_media.id)
          : "";
        const existing =
          mediaId &&
          gallery?.querySelector(
            `.product__media-item[data-media-id="${CSS.escape(mediaId)}"]`
          );

        // Only slide within the current shade gallery. Different shade must rebuild.
        if (
          existing &&
          nextKey &&
          nextKey === this.activeMediaGalleryKey &&
          typeof gallery.setActiveMedia === "function"
        ) {
          gallery.dataset.skipNextFade = "true";
          gallery.classList.add("is-instant-media");
          gallery.setActiveMedia(variant);
          setTimeout(() => {
            gallery.classList.remove("is-instant-media");
            delete gallery.dataset.skipNextFade;
          }, 50);
          return;
        }

        // Instant path: rebuild shade gallery from page media JSON (no section fetch).
        if (this.applyClientMediaGallery(variant)) {
          return;
        }

        this.preloadFeaturedMedia(variant);
        this.scheduleMediaGallerySync(productUrl, variant.id);
      }

      getProductMedia() {
        if (this.productMediaCache) return this.productMediaCache;

        const script =
          this.querySelector("[data-product-media]") ||
          document.querySelector(
            `#MainProduct-${this.sectionId} [data-product-media], [data-product-media]`
          );
        if (!script?.textContent) {
          this.productMediaCache = [];
          return this.productMediaCache;
        }

        try {
          this.productMediaCache = JSON.parse(script.textContent);
        } catch (error) {
          console.error(error);
          this.productMediaCache = [];
        }
        return this.productMediaCache;
      }

      getShadeAltFromVariant(variant) {
        // Same as product-media-gallery.liquid: no shade option → first option.
        const shadeIndex = Math.max(this.getShadeOptionIndex(), 0);
        if (variant?.options?.[shadeIndex] != null) {
          return String(variant.options[shadeIndex]).trim();
        }
        return String(variant?.featured_media?.alt || "").trim();
      }

      getMediaShadeValues(value) {
        return String(value || "")
          .split(/[,，]/)
          .map((shade) => shade.trim())
          .filter(Boolean);
      }

      isVariantIdToken(value) {
        return /^[1-9]\d{7,}$/.test(String(value || ""));
      }

      hasVariantIdMedia() {
        if (this.variantIdMediaFlag === undefined) {
          this.variantIdMediaFlag = this.getProductMedia().some((item) =>
            this.getMediaShadeValues(item.alt).some((token) =>
              this.isVariantIdToken(token)
            )
          );
        }
        return this.variantIdMediaFlag;
      }

      // Bundle pages lock the gallery unless image ALT groups media by variant id.
      isVariantMediaLocked() {
        return (
          this.dataset.lockVariantMedia === "true" && !this.hasVariantIdMedia()
        );
      }

      getBundleComponentData(group) {
        const script = group.querySelector("[data-bundle-component-images]");
        if (!script) return null;
        if (!this.bundleComponentDataCache) {
          this.bundleComponentDataCache = new WeakMap();
        }
        if (!this.bundleComponentDataCache.has(script)) {
          let data = null;
          try {
            data = JSON.parse(script.textContent);
          } catch (error) {
            data = null;
          }
          this.bundleComponentDataCache.set(script, data);
        }
        return this.bundleComponentDataCache.get(script);
      }

      // Variant id plus, for Bundle parents, the component variant ids of each option.
      getMediaVariantIds(variant) {
        if (!variant?.id) return [];
        const ids = [String(variant.id)];
        this.querySelectorAll("[data-bundle-option]").forEach((group, index) => {
          const value = variant.options?.[index];
          const componentId = this.getBundleComponentData(group)?.[value]?.variant_id;
          if (componentId) ids.push(String(componentId));
        });
        return ids;
      }

      // Keep in sync with snippets/tp-media-variant-match.liquid.
      filterMediaForVariant(variant) {
        const media = this.getProductMedia();
        if (!media.length) return [];
        const shade = this.getShadeAltFromVariant(variant);
        const variantIds = this.getMediaVariantIds(variant);
        const matched = media.filter((item) => {
          const alt = String(item.alt || "").trim();
          if (alt === "") return true;
          return this.getMediaShadeValues(alt).some(
            (token) => token === shade || variantIds.includes(token)
          );
        });
        // Nothing matched: show every media instead of an empty gallery.
        return matched.length ? matched : media;
      }

      buildSizedMediaSrc(src, width) {
        if (!src) return "";
        try {
          const url = new URL(src, window.location.origin);
          url.searchParams.set("width", String(width));
          return url.toString();
        } catch (error) {
          const base = String(src).split("?")[0];
          return `${base}?width=${width}`;
        }
      }

      buildMediaSrcset(src) {
        const widths = [400, 620, 690, 810, 980, 1240, 1380, 1620, 1960];
        return widths
          .map((width) => {
            const sized = this.buildSizedMediaSrc(src, width);
            return sized ? `${sized} ${width}w` : "";
          })
          .filter(Boolean)
          .join(", ");
      }

      buildMediaSlideHtml(media, index, isActive, sizes) {
        const rawAlt = this.escapeHtml(media.alt || "");
        const alt = this.escapeHtml(media.display_alt ?? media.alt ?? "");
        const src = this.buildSizedMediaSrc(media.src, isActive ? 1240 : 980);
        const srcset = this.buildMediaSrcset(media.src);
        const fullSrc = this.buildSizedMediaSrc(media.src, 1946);
        const aspect = media.aspect_ratio || 1;
        const pswpHeight = Math.ceil(
          (Number(media.width) || 1946) / Number(aspect || 1)
        );
        const loading = isActive ? "eager" : "lazy";
        const fetchpriority = isActive ? "high" : "low";
        const status = isActive ? "fade-in" : "";
        const activeClass = isActive ? " fade-in swiper-slide-active" : "";
        const mediaType = media.media_type || "image";
        const zoomEnabled =
          this.mediaGallery?.dataset?.enableImageZoom === "true";
        const zoomHtml = zoomEnabled
          ? `<span class="product__media-icon" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" class="icon icon-zoom icon--medium" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M10.875 18.75C15.2242 18.75 18.75 15.2242 18.75 10.875C18.75 6.52576 15.2242 3 10.875 3C6.52576 3 3 6.52576 3 10.875C3 15.2242 6.52576 18.75 10.875 18.75Z"/><path d="M16.4434 16.4453L20.9997 21.0016"/></svg></span><button class="product__media-zoom btn--inherit js-photoswipe--zoom" type="button" aria-haspopup="dialog" data-media-id="${media.id}" data-media-type="${mediaType}" data-media-index="${index}"><span class="visually-hidden">Open media ${index + 1}</span></button>`
          : "";

        return `<div role="listitem" tabindex="-1" class="product__media-item swiper-slide${activeClass}" data-media-id="${media.id}" data-alt="${rawAlt}" data-media-type="${mediaType}" data-media-index="${index}" data-src="${fullSrc}" data-pswp-width="${media.width || 1946}" data-pswp-height="${pswpHeight}" data-status="${status}">
  <div class="product__media-container blocks-radius media-type-image" style="--aspect-ratio: ${aspect};">
    <motion-element data-motion="zoom-out-sm" class="product__media media-wrapper">
      <img src="${src}" srcset="${srcset}" sizes="${sizes}" alt="${alt}" loading="${loading}" fetchpriority="${fetchpriority}" width="${media.width || 1946}" height="${media.height || pswpHeight}" is="image-lazy">
    </motion-element>
    ${zoomHtml}
  </div>
</div>`;
      }

      buildThumbHtml(media, index, isActive) {
        const alt = this.escapeHtml(media.display_alt ?? media.alt ?? "");
        const src = this.buildSizedMediaSrc(media.src, 150);
        const activeClass = isActive
          ? " swiper-slide-thumb-active swiper-slide-active"
          : "";
        return `<div class="product__thumbs-item swiper-slide${activeClass}" data-target="${media.id}" data-media-position="${index + 1}">
  <div class="product__thumbs-container medium-blocks-radius media-wrapper">
    <div class="product__thumbs-inner" style="--aspect-ratio: 1">
      <img src="${src}" alt="${alt}" loading="${isActive ? "eager" : "lazy"}" width="150" height="150" sizes="(max-width: 767px) calc((100vw - 30px) / 4), (max-width: 1023px) 70px, 150px" is="image-lazy">
    </div>
  </div>
</div>`;
      }

      escapeHtml(value) {
        return String(value || "")
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;")
          .replace(/'/g, "&#39;");
      }

      applyClientMediaGallery(variant) {
        const gallery = this.mediaGallery;
        if (!gallery) return false;

        const items = this.filterMediaForVariant(variant);
        if (!items.length) return false;

        // Videos/models still need the Liquid section path for correct players.
        if (items.some((item) => item.media_type && item.media_type !== "image")) {
          return false;
        }

        const list = gallery.querySelector('[id^="Slider-Gallery"]');
        if (!list) return false;

        const thumbs = gallery.querySelector('[id^="Slider-Thumbnails"]');
        const sampleImg = gallery.querySelector(".product__media-item img");
        const sizes =
          sampleImg?.getAttribute("sizes") ||
          "(max-width: 768px) calc(100vw - 3rem), 50vw";

        // Keep Liquid / product.media order. First filtered item is first-show.
        const activeIndex = 0;
        const oldScrollY = window.scrollY;

        gallery.dataset.skipNextFade = "true";
        gallery.classList.add("is-instant-media");
        gallery.dataset.initialMediaId = String(items[activeIndex].id);
        gallery.dataset.onlyImage = items.length < 2 ? "true" : "false";

        // Warm the first main image before paint when possible.
        const firstSrc = this.buildSizedMediaSrc(items[activeIndex].src, 1240);
        if (firstSrc) {
          const warm = new Image();
          warm.decoding = "async";
          warm.src = firstSrc;
        }

        list.innerHTML = items
          .map((media, index) =>
            this.buildMediaSlideHtml(media, index, index === activeIndex, sizes)
          )
          .join("");

        if (thumbs) {
          thumbs.innerHTML = items
            .map((media, index) =>
              this.buildThumbHtml(media, index, index === activeIndex)
            )
            .join("");
        }

        if (typeof gallery.reinitAfterDomSwap === "function") {
          gallery.reinitAfterDomSwap();
        } else if (typeof gallery.init === "function") {
          gallery.sliderInstance = false;
          gallery.thumbsInstance = false;
          gallery.init();
        }

        this.activeMediaGalleryKey = this.getMediaGalleryCacheKey(variant);

        requestAnimationFrame(() => {
          window.scrollTo({ top: oldScrollY, behavior: "instant" });
          gallery.applyInitialMedia?.();
          const activeSlide =
            gallery.querySelector(
              '.product__media-item.swiper-slide-active, .product__media-item[data-status="fade-in"]'
            ) || gallery.querySelector(".product__media-item");
          if (activeSlide) {
            activeSlide.dataset.status = "fade-in";
            activeSlide.classList.add("fade-in", "swiper-slide-active");
          }
          setTimeout(() => {
            gallery.classList.remove("is-instant-media");
            delete gallery.dataset.skipNextFade;
          }, 80);
        });

        return true;
      }

      getMediaGalleryCacheKey(variant) {
        // ALT with variant ids: PCS-only changes can change the media set too.
        if (variant?.id && this.hasVariantIdMedia()) return `variant:${variant.id}`;
        const shade = this.getShadeFromVariant(variant);
        if (shade) return `shade:${shade}`;
        const alt = String(variant?.featured_media?.alt || "").trim();
        if (alt) return `alt:${alt}`;
        return variant?.id ? `variant:${variant.id}` : "";
      }

      getFeaturedMediaPreviewSrc(variant, width = 1240) {
        const preview =
          variant?.featured_media?.preview_image || variant?.featured_media;
        const src = preview?.src;
        if (!src) return "";

        try {
          const url = new URL(src, window.location.origin);
          url.searchParams.set("width", String(width));
          return url.toString();
        } catch (error) {
          const base = String(src).split("?")[0];
          return `${base}?width=${width}`;
        }
      }

      preloadFeaturedMedia(variant) {
        const previewSrc = this.getFeaturedMediaPreviewSrc(variant, 1240);
        if (!previewSrc) return;
        const warm = new Image();
        warm.decoding = "async";
        warm.src = previewSrc;
      }

      bindMediaGalleryPrefetch() {
        if (!this.variantSelectors) return;
        this.onMediaGalleryPrefetchPointerEnter = (event) => {
          const host = event.target?.closest?.(
            'variant-selects label, variant-selects input[type="radio"]'
          );
          if (!host) return;

          let input = host;
          if (host.tagName === "LABEL") {
            const forId = host.getAttribute("for");
            input = forId
              ? this.variantSelectors.querySelector(`#${CSS.escape(forId)}`)
              : host.querySelector('input[type="radio"]');
          }

          if (!input || input.type !== "radio" || input.disabled) return;
          this.prefetchMediaGalleryForInput(input);
        };
        this.variantSelectors.addEventListener(
          "pointerenter",
          this.onMediaGalleryPrefetchPointerEnter,
          true
        );

        this.onVariantTabsChange = () => {
          this.prefetchVisibleShadeGalleries(6);
        };
        this.addEventListener("variant-tabs:change", this.onVariantTabsChange);
      }

      scheduleIdleShadeGalleryPrefetch() {
        const run = () => this.prefetchVisibleShadeGalleries(4);
        if (typeof requestIdleCallback === "function") {
          this.idleShadePrefetchHandle = requestIdleCallback(run, {
            timeout: 2500,
          });
        } else {
          setTimeout(run, 1200);
        }
      }

      prefetchVisibleShadeGalleries(limit = 4) {
        if (!this.variantSelectors || this.isVariantMediaLocked()) {
          return;
        }

        const inputs = [
          ...this.variantSelectors.querySelectorAll(
            'fieldset input[type="radio"]:not(.is-tab-hidden):not(.is-pcs-hidden)'
          ),
        ];
        let queued = 0;
        for (const input of inputs) {
          if (queued >= limit) break;
          const variant = this.getVariantFromOptionInput(input);
          const cacheKey = this.getMediaGalleryCacheKey(variant);
          if (
            !cacheKey ||
            this.mediaGalleryHtmlCache.has(cacheKey) ||
            this.mediaGalleryPrefetching.has(cacheKey)
          ) {
            continue;
          }
          this.prefetchMediaGalleryForInput(input);
          queued += 1;
        }
      }

      getVariantFromOptionInput(input) {
        if (!input) return null;
        const optionNames = this.getProductOptionNames();
        if (!optionNames.length) return null;

        const selectedValues = this.getSelectedOptionValuesFromDom();
        const name = String(input.name || "").trim();
        const positionMatch = name.match(/-(\d+)$/);
        const position = positionMatch ? Number(positionMatch[1]) : NaN;
        if (!Number.isFinite(position) || position < 1) return null;

        selectedValues[position - 1] = input.value;
        if (selectedValues.some((value) => value == null || value === "")) {
          return null;
        }

        const variants = this.getProductVariants();
        return (
          variants.find((variant) =>
            variant.options?.every(
              (optionValue, index) => optionValue === selectedValues[index]
            )
          ) || null
        );
      }

      prefetchMediaGalleryForInput(input) {
        const variant = this.getVariantFromOptionInput(input);
        if (!variant?.id) return;

        // Prefer warming the first shade image from page media JSON (no network HTML).
        const items = this.filterMediaForVariant(variant);
        if (items[0]?.src) {
          const warm = new Image();
          warm.decoding = "async";
          warm.src = this.buildSizedMediaSrc(items[0].src, 1240);
          return;
        }

        const productUrl = this.dataset.url;
        if (!productUrl) return;

        const cacheKey = this.getMediaGalleryCacheKey(variant);
        if (!cacheKey) return;
        if (this.mediaGalleryHtmlCache.has(cacheKey)) return;
        if (this.mediaGalleryPrefetching.has(cacheKey)) return;

        this.mediaGalleryPrefetching.add(cacheKey);
        this.fetchMediaGalleryHtml(productUrl, variant.id)
          .then((galleryHtml) => {
            if (galleryHtml) this.mediaGalleryHtmlCache.set(cacheKey, galleryHtml);
          })
          .finally(() => {
            this.mediaGalleryPrefetching.delete(cacheKey);
          });
      }

      fetchMediaGalleryHtml(productUrl, variantId, signal) {
        const requestUrl = `${productUrl}?section_id=${this.sectionId}&variant=${variantId}`;
        return fetch(requestUrl, signal ? { signal } : undefined)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, "text/html");
            return html.querySelector("media-gallery")?.outerHTML || null;
          });
      }

      scheduleMediaGallerySync(productUrl, variantId) {
        if (!variantId || this.isVariantMediaLocked()) return;

        clearTimeout(this.mediaGallerySyncTimer);
        // Do not abort an in-flight fetch for the same variant — rapid re-clicks
        // were cancelling the only request and looking like "no response".
        if (
          this.mediaGallerySyncAbort &&
          this.mediaGallerySyncVariantId &&
          this.mediaGallerySyncVariantId !== variantId
        ) {
          this.mediaGallerySyncAbort.abort();
        }

        const variant = this.currentVariant;
        const cacheKey = this.getMediaGalleryCacheKey(variant);
        const cachedHtml = cacheKey
          ? this.mediaGalleryHtmlCache.get(cacheKey)
          : null;

        if (cachedHtml) {
          const wrapper = document.createElement("div");
          wrapper.innerHTML = cachedHtml;
          this.replaceMediaGallery(
            { querySelector: (selector) => wrapper.querySelector(selector) },
            variant
          );
          return;
        }

        this.mediaGallerySyncTimer = setTimeout(() => {
          this.mediaGallerySyncRequestId = (this.mediaGallerySyncRequestId || 0) + 1;
          const requestId = this.mediaGallerySyncRequestId;
          this.mediaGallerySyncVariantId = variantId;
          this.mediaGallerySyncAbort = new AbortController();

          this.fetchMediaGalleryHtml(
            productUrl,
            variantId,
            this.mediaGallerySyncAbort.signal
          )
            .then((galleryHtml) => {
              if (requestId !== this.mediaGallerySyncRequestId) return;
              if (this.currentVariant?.id !== variantId) return;
              if (!galleryHtml) return;

              if (cacheKey) this.mediaGalleryHtmlCache.set(cacheKey, galleryHtml);

              const wrapper = document.createElement("div");
              wrapper.innerHTML = galleryHtml;
              this.replaceMediaGallery(
                { querySelector: (selector) => wrapper.querySelector(selector) },
                this.currentVariant
              );
            })
            .catch((error) => {
              if (error.name === "AbortError") return;
              console.error(error);
            })
            .finally(() => {
              if (this.mediaGallerySyncVariantId === variantId) {
                this.mediaGallerySyncVariantId = null;
              }
            });
        }, 0);
      }

      prepareGalleryForVariant(galleryNode, variant) {
        const list = galleryNode.querySelector('[id^="Slider-Gallery"]');
        if (!list) return null;

        const items = [...list.querySelectorAll(".product__media-item")];
        if (!items.length) return null;

        const featuredId = variant?.featured_media?.id
          ? String(variant.featured_media.id)
          : "";
        const featuredAlt = variant?.featured_media?.alt
          ? String(variant.featured_media.alt)
          : "";

        // Keep Liquid / product.media order. Prefer the server-marked first-show slide
        // so the final paint matches the shade gallery, not a temporary featured_media hack.
        let active =
          items.find((el) => el.dataset.status === "fade-in") ||
          (featuredId &&
            items.find((el) => el.dataset.mediaId === featuredId)) ||
          (featuredAlt &&
            items.find((el) => el.dataset.alt === featuredAlt)) ||
          items[0];

        items.forEach((el) => {
          el.dataset.status = "";
          el.classList.remove("swiper-slide-active", "fade-in");
        });

        active.dataset.status = "fade-in";
        active.classList.add("fade-in", "swiper-slide-active");

        const activeImg = active.querySelector("img");
        if (activeImg) {
          activeImg.loading = "eager";
          if ("fetchPriority" in activeImg) activeImg.fetchPriority = "high";
        }

        const thumbs = galleryNode.querySelector('[id^="Slider-Thumbnails"]');
        if (thumbs && active.dataset.mediaId) {
          thumbs.querySelectorAll(".product__thumbs-item").forEach((thumb) => {
            thumb.classList.remove(
              "swiper-slide-thumb-active",
              "swiper-slide-active"
            );
          });
          const activeThumb = thumbs.querySelector(
            `[data-target="${active.dataset.mediaId}"]`
          );
          if (activeThumb) {
            activeThumb.classList.add(
              "swiper-slide-thumb-active",
              "swiper-slide-active"
            );
          }
        }

        galleryNode.dataset.initialMediaId = active.dataset.mediaId || "";
        return active;
      }

      replaceMediaGallery(html, variant = this.currentVariant) {
        if (this.isVariantMediaLocked()) return;

        const source = html?.querySelector("media-gallery");
        const destination = this.mediaGallery;
        if (!source || !destination?.parentNode) return;

        const oldScrollY = window.scrollY;
        const wrapper = document.createElement("div");
        wrapper.innerHTML = source.outerHTML;
        const newNode = wrapper.firstElementChild;
        if (!newNode) return;

        newNode.querySelectorAll("script").forEach((script) => script.remove());
        this.prepareGalleryForVariant(newNode, variant);

        // One-step visible replace. Never keep two media-gallery nodes in the DOM:
        // duplicate ids + init-while-hidden breaks Swiper (no slide / no variant sync).
        newNode.dataset.skipNextFade = "true";
        newNode.classList.add("is-instant-media");
        destination.replaceWith(newNode);

        this.activeMediaGalleryKey = this.getMediaGalleryCacheKey(variant);

        requestAnimationFrame(() => {
          const gallery = this.mediaGallery;
          if (!gallery) return;

          gallery.dataset.skipNextFade = "true";
          gallery.classList.add("is-instant-media");

          if (typeof gallery.applyInitialMedia === "function") {
            gallery.applyInitialMedia();
          } else if (typeof gallery.syncFadeStatusToIndex === "function") {
            const activeIndex = gallery.sliderInstance?.slider?.activeIndex || 0;
            gallery.syncFadeStatusToIndex(activeIndex);
            gallery.ensureSlideImageEager?.(activeIndex);
          }

          gallery.sliderInstance?.slider?.update?.();
          gallery.thumbsInstance?.slider?.update?.();

          // Make sure the visible main slide is opaque even if Swiper classes lag.
          const activeSlide =
            gallery.querySelector(
              '.product__media-item.swiper-slide-active, .product__media-item[data-status="fade-in"]'
            ) || gallery.querySelector(".product__media-item");
          if (activeSlide) {
            activeSlide.dataset.status = "fade-in";
            activeSlide.classList.add("fade-in", "swiper-slide-active");
            const img = activeSlide.querySelector("img");
            if (img) {
              img.loading = "eager";
              if ("fetchPriority" in img) img.fetchPriority = "high";
            }
          }

          window.scrollTo({ top: oldScrollY, behavior: "instant" });

          setTimeout(() => {
            gallery.classList.remove("is-instant-media");
            delete gallery.dataset.skipNextFade;
          }, 80);
        });
      }

      scheduleBackgroundVariantSync() {
        // Badges update on the client path via updateBadgesFromVariant.
      }

      patchSecondaryVariantData() {
        // Kept for compatibility with older call sites; no longer used by client updates.
      }

      handleSwapProduct(productUrl, updateFullPage, viewMode) {
        return (html) => {
          const quickView = html.querySelector(
            "#MainProduct-quick-view__content"
          );
          if (quickView && viewMode === "quick-view") {
            html = quickView.content.cloneNode(true);
          }
          const selector = updateFullPage
            ? "product-info[id^='MainProduct']"
            : "product-info";
          const variant = this.getSelectedVariant(html.querySelector(selector));

          this.updateURL(productUrl, variant?.id, variant, html.querySelector(selector));

          if (updateFullPage) {
            document.querySelector("head title").innerHTML =
              html.querySelector("head title").innerHTML;
            HTMLUpdateUtility.viewTransition(
              document.querySelector("main"),
              html.querySelector("main"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
            HTMLUpdateUtility.viewTransition(
              document.getElementById("shopify-section-sticky-atc-bar"),
              html.getElementById("shopify-section-sticky-atc-bar"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector("product-info"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }

          this.currentVariant = variant;
        };
      }

      handleUpdateProductInfo(productUrl, viewMode) {
        return (html) => {
          const quickView = html.querySelector(
            "#MainProduct-quick-view__content"
          );
          const isQuickViewMode = viewMode === "quick-view"
          if (quickView && viewMode === "quick-view") {
            html = quickView.content.cloneNode(true);
          }
          const quickViewContentDom = this.findParent(this, 'quick-view__content')

          const variant = this.getSelectedVariant(html);
          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.syncVariantSkuBadges(variant);
          this.updateURL(productUrl, variant?.id, variant, html);
          this.updateShareUrl(variant?.id, variant, html);
          this.updateVariantInputs(variant?.id);

          if (isQuickViewMode && quickViewContentDom) {
            requestAnimationFrame(() => {
              quickViewContentDom.scrollTo({ top: 0 });
            });
          }

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.replaceMediaGallery(html, variant);

          const updateSourceFromDestination = (
            id,
            shouldHide = (source) => false
          ) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(
              `#${id}-${this.dataset.section}`
            );

            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle("hidden", shouldHide(source));
            }
          };

          updateSourceFromDestination("price");
          updateSourceFromDestination("Sku", ({ classList }) =>
            classList.contains("hidden")
          );
          updateSourceFromDestination(
            "Inventory",
            ({ innerText }) => innerText === ""
          );
          updateSourceFromDestination("Badges", ({ classList }) =>
            classList.contains("hidden")
          );
          updateSourceFromDestination("PricePerItem", ({ classList }) =>
            classList.contains("hidden")
          );
          updateSourceFromDestination("Volume");

          this.updateQuantityRules(this.sectionId, this.productId, html);
          this.querySelector(
            `#QuantityRules-${this.dataset.section}`
          )?.classList.remove("hidden");
          this.querySelector(
            `#VolumeNote-${this.dataset.section}`
          )?.classList.remove("hidden");

          const addButtonUpdated = html.getElementById(
            `ProductSubmitButton-${this.sectionId}`
          );

          this.toggleAddButton(
            addButtonUpdated ? addButtonUpdated.hasAttribute("disabled") : true,
            FoxTheme.variantStrings.soldOut
          );

          FoxTheme.pubsub.publish(
            FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
            {
              data: {
                sectionId: this.sectionId,
                html,
                variant,
              },
            }
          );

          document.dispatchEvent(
            new CustomEvent("variant:changed", {
              detail: {
                variant: variant,
              },
            })
          );
        };
      }

      findParent(parentElement, className) {
        if (!parentElement || !className) return null
        if (parentElement.classList.contains(className)) return parentElement
        return this.findParent(parentElement.parentElement, className)
      }

      buildRequestUrlWithParams(
        url,
        optionValues,
        shouldFetchFullPage = false
      ) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(",")}`);
        }

        return `${url}?${params.join("&")}`;
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector(
          "variant-selects [data-selected-variant]"
        )?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      getProductOptionNames(productInfoNode = this) {
        if (productInfoNode === this && this._cachedOptionNames) {
          return this._cachedOptionNames;
        }

        const optionNames = productInfoNode.querySelector(
          "variant-selects [data-product-options]"
        )?.innerHTML;
        const parsed = !!optionNames ? JSON.parse(optionNames) : [];
        if (productInfoNode === this) this._cachedOptionNames = parsed;
        return parsed;
      }

      getProductVariants(productInfoNode = this) {
        if (productInfoNode === this && this._cachedProductVariants) {
          return this._cachedProductVariants;
        }

        const variants = productInfoNode.querySelector(
          "variant-selects [data-product-variants]"
        )?.innerHTML;
        const parsed = !!variants ? JSON.parse(variants) : [];
        if (productInfoNode === this) this._cachedProductVariants = parsed;
        return parsed;
      }

      getProductOptionValues() {
        if (this._cachedProductOptionValues) return this._cachedProductOptionValues;

        const json = this.variantSelectors?.querySelector(
          "[data-product-option-values]"
        )?.textContent;
        if (!json) {
          this._cachedProductOptionValues = [];
          return this._cachedProductOptionValues;
        }

        try {
          this._cachedProductOptionValues = JSON.parse(json);
        } catch (error) {
          this._cachedProductOptionValues = [];
        }
        return this._cachedProductOptionValues;
      }

      getShadeOptionIndex(productInfoNode = this) {
        const optionNames = this.getProductOptionNames(productInfoNode);
        const shadeNames = ["shade", "color", "colour", "\u8272\u53f7", "\u989c\u8272"];

        return optionNames.findIndex((name) => {
          const normalizedName = String(name || "").trim().toLowerCase();
          return shadeNames.some((shadeName) => normalizedName.includes(shadeName));
        });
      }

      slugifyShade(value) {
        return String(value || "")
          .trim()
          .toLowerCase()
          .normalize("NFKD")
          .replace(/[\u0300-\u036f]/g, "")
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, "");
      }

      getShadeFromVariant(variant, productInfoNode = this) {
        if (!variant) return "";
        const shadeOptionIndex = this.getShadeOptionIndex(productInfoNode);
        if (shadeOptionIndex < 0) return "";
        return this.slugifyShade(variant.options?.[shadeOptionIndex]);
      }

      getVariantFromShade(shade, productInfoNode = this) {
        const shadeOptionIndex = this.getShadeOptionIndex(productInfoNode);
        if (shadeOptionIndex < 0 || !shade) return null;

        const variants = this.getProductVariants(productInfoNode);
        return variants.find((variant) => {
          return this.slugifyShade(variant.options?.[shadeOptionIndex]) === shade;
        });
      }

      selectControlsForVariant(variant) {
        if (!variant || !this.variantSelectors) return null;

        let eventTarget = null;
        const optionNames = this.getProductOptionNames();

        variant.options.forEach((optionValue, index) => {
          const optionName = optionNames[index];
          const optionPosition = index + 1;
          const select = Array.from(this.variantSelectors.querySelectorAll("select")).find((element) => {
            return element.name === `options[${optionName}]`;
          });

          if (select) {
            select.value = optionValue;
            Array.from(select.options).forEach((option) => {
              option.toggleAttribute("selected", option.value === optionValue);
            });
            eventTarget = eventTarget || select;
            return;
          }

          const matchingInputs = Array.from(
            this.variantSelectors.querySelectorAll("fieldset input")
          ).filter((input) => {
            return (
              input.name.trim().endsWith(`-${optionPosition}`) &&
              input.value === optionValue
            );
          });

          matchingInputs.forEach((input) => {
            input.checked = true;
          });

          if (matchingInputs.length) {
            eventTarget = eventTarget || matchingInputs[0];
          }
        });

        return eventTarget;
      }

      applyShadeFromUrl() {
        if (this.dataset.updateUrl === "false" || !this.variantSelectors) return;

        const searchParams = new URLSearchParams(window.location.search);
        if (searchParams.has("variant")) return;

        const shade = this.slugifyShade(searchParams.get("shade"));
        if (!shade) return;

        const variant = this.getVariantFromShade(shade);
        if (!variant) return;

        requestAnimationFrame(() => {
          const eventTarget = this.selectControlsForVariant(variant);
          this.currentVariant = variant;
          this.updateVariantInputs(variant.id);
          this.updateURL(this.dataset.url, variant.id, variant);

          if (eventTarget) {
            eventTarget.dispatchEvent(new Event("change", { bubbles: true }));
          }
        });
      }

      getHtmlByRequestUrlWithCache(requestUrl) {
        if (!requestUrl) {
          return Promise.reject('Empty requestUrl');
        }
        const cacheData = this.htmlCacheForRequestUrl.get(requestUrl)
        if (cacheData) {
          const cacheTime = cacheData.cacheTime
          if (cacheTime && Date.now() - cacheTime <= this.htmlCacheDuration) {
            return Promise.resolve(cacheData.data)
          }
        }
        return fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.htmlCacheForRequestUrl.set(requestUrl, {
              data: responseText,
              cacheTime: Date.now()
            })

            return responseText
          })
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.optionChangeRequestId += 1;
        const requestId = this.optionChangeRequestId;
        this.abortController?.abort();
        this.abortController = new AbortController();
        this.getHtmlByRequestUrlWithCache(requestUrl)
          .then((responseText) => {
            if (requestId !== this.optionChangeRequestId) return;
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            callback(html);
          })
          .then(() => {
            if (requestId !== this.optionChangeRequestId || !targetId) return;
            document.getElementById(targetId)?.focus({ preventScroll: true });
          })
          .catch((error) => {
            if (error.name === "AbortError") return;
            console.error(error);
          });
      }

      updateMediaGallery(html) {
        this.replaceMediaGallery(html, this.currentVariant);
      }

      updateOptionValues(html) {
        const source = html.querySelector("variant-selects");
        const destination = this.variantSelectors;
        if (!source || !destination) return;

        const sourceInputCount = source.querySelectorAll("fieldset input").length;
        const destinationInputCount = destination.querySelectorAll("fieldset input").length;
        const sourcePickerCount = source.querySelectorAll("variant-select-picker .option").length;
        const destinationPickerCount = destination.querySelectorAll("variant-select-picker .option").length;

        if (
          sourceInputCount !== destinationInputCount ||
          sourcePickerCount !== destinationPickerCount
        ) {
          this.replaceVariantSelectsContent(destination, source);
          return;
        }

        this.patchVariantSelects(destination, source);
      }

      copyJsonScript(destination, source, selector) {
        const from = source.querySelector(selector);
        const to = destination.querySelector(selector);
        if (from && to) to.textContent = from.textContent;
      }

      replaceVariantSelectsContent(destination, source) {
        window.shopifyTmpVars = window.shopifyTmpVars || {};
        if (window.shopifyTmpVars.productVariantPickerTabActiveIndex == null) {
          window.shopifyTmpVars.productVariantPickerTabActiveIndex = 0;
        }
        const clone = source.cloneNode(true);
        clone.querySelectorAll("script").forEach((script) => {
          const type = (script.getAttribute("type") || "").toLowerCase();
          if (type !== "application/json") script.remove();
        });

        destination.innerHTML = clone.innerHTML;
        this._cachedOptionNames = null;
        this._cachedProductVariants = null;
        this._cachedProductOptionValues = null;
        this._variantSkuTags = null;
        ["data-dependent-option-values", "data-url", "data-update-url"].forEach((attr) => {
          if (source.hasAttribute(attr)) {
            destination.setAttribute(attr, source.getAttribute(attr));
          }
        });
      }

      patchVariantSelects(destination, source) {
        this.copyJsonScript(destination, source, "[data-selected-variant]");
        this.copyJsonScript(destination, source, "[data-product-variants]");
        this.copyJsonScript(destination, source, "[data-product-option-values]");
        this.copyJsonScript(destination, source, "[data-product-options]");
        this.copyJsonScript(destination, source, "[data-variant-sku-tags]");
        this._cachedOptionNames = null;
        this._cachedProductVariants = null;
        this._cachedProductOptionValues = null;
        this._variantSkuTags = null;

        const sourceInputs = new Map();
        source.querySelectorAll("input[data-option-value-id]").forEach((input) => {
          sourceInputs.set(input.dataset.optionValueId, input);
        });

        destination.querySelectorAll("input[data-option-value-id]").forEach((input) => {
          const sourceInput = sourceInputs.get(input.dataset.optionValueId);
          if (!sourceInput) return;

          input.checked = sourceInput.checked;
          input.toggleAttribute("checked", sourceInput.checked);
          input.disabled = sourceInput.disabled;
          const wasTabHidden = input.classList.contains("is-tab-hidden");
          input.className = sourceInput.className;
          input.classList.toggle("is-tab-hidden", wasTabHidden);
          if (sourceInput.dataset.productUrl) {
            input.dataset.productUrl = sourceInput.dataset.productUrl;
          }
        });

        destination.querySelectorAll("[data-selected-swatch-value]").forEach((label) => {
          const optionName = label.getAttribute("data-selected-swatch-value");
          const sourceLabel = source.querySelector(
            `[data-selected-swatch-value="${CSS.escape(optionName)}"]`
          );
          if (sourceLabel) label.textContent = sourceLabel.textContent;
        });

        source.querySelectorAll("variant-select-picker .option[data-option-value-id]").forEach((sourceOption) => {
          const destinationOption = destination.querySelector(
            `variant-select-picker .option[data-option-value-id="${sourceOption.dataset.optionValueId}"]`
          );
          if (!destinationOption) return;
          destinationOption.className = sourceOption.className;
        });

        const sourcePickerInfo = source.querySelector("variant-select-picker .value-info");
        const destinationPickerInfo = destination.querySelector("variant-select-picker .value-info");
        if (sourcePickerInfo && destinationPickerInfo) {
          destinationPickerInfo.innerHTML = sourcePickerInfo.innerHTML;
        }

        source.querySelectorAll("select").forEach((sourceSelect) => {
          if (!sourceSelect.id) return;
          const destinationSelect = destination.querySelector(`#${CSS.escape(sourceSelect.id)}`);
          if (!destinationSelect) return;
          destinationSelect.value = sourceSelect.value;
          Array.from(destinationSelect.options).forEach((option) => {
            const sourceOption = sourceSelect.querySelector(
              `option[data-option-value-id="${option.dataset.optionValueId}"]`
            );
            option.toggleAttribute("selected", Boolean(sourceOption?.hasAttribute("selected")));
            option.disabled = Boolean(sourceOption?.disabled);
          });
        });
      }

      updateURL(url, variantId, variant, productInfoNode = this) {
        if (this.dataset.updateUrl === "false") return;
        const nextUrl = new URL(url, window.location.origin);
        const shade = this.getShadeFromVariant(variant, productInfoNode);

        if (variantId) {
          nextUrl.searchParams.set("variant", variantId);
        } else {
          nextUrl.searchParams.delete("variant");
        }

        if (shade) {
          nextUrl.searchParams.set("shade", shade);
        } else {
          nextUrl.searchParams.delete("shade");
        }

        window.history.replaceState(
          {},
          "",
          `${nextUrl.pathname}${nextUrl.search}`
        );
      }

      updateVariantInputs(variantId) {
        document
          .querySelectorAll(
            `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
          )
          .forEach((productForm) => {
            const input = productForm.querySelector('input[name="id"]');
            input.value = variantId ?? "";
            input.dispatchEvent(new Event("change", { bubbles: true }));
          });
        this.updateByobLinks(variantId);
      }

      updateByobLinks(variantId) {
        document
          .querySelectorAll(`#product-form-${this.dataset.section} [data-product-byob-link]`)
          .forEach((link) => {
            const baseUrl = link.dataset.byobBaseUrl || link.href.split("?")[0];
            if (!variantId) {
              link.removeAttribute("href");
              link.setAttribute("aria-disabled", "true");
              return;
            }

            const separator = baseUrl.includes("?") ? "&" : "?";
            link.href = `${baseUrl}${separator}byob_variant=${variantId}`;
            link.dataset.byobVariantId = variantId;
            link.removeAttribute("aria-disabled");
          });
      }

      updateMedia(variant, inited) {
        if (inited && this.isVariantMediaLocked()) return;
        const productMedia = this.querySelector(
          `[id^="MediaGallery-${this.dataset.section}"]`
        );
        if (!productMedia || typeof productMedia.setActiveMedia !== "function") return;

        if (productMedia.sliderInstance === false) {
          productMedia.init();
        }

        if (inited) {
          productMedia.setActiveMedia(variant);
        }
      }

      updateShareUrl(variantId, variant, productInfoNode = this) {
        if (!variantId) return;
        const shareButton = document.getElementById(
          `ProductShare-${this.dataset.section}`
        );
        if (!shareButton || !shareButton.updateUrl) return;
        const shareUrl = new URL(this.dataset.url, window.shopUrl || window.location.origin);
        const shade = this.getShadeFromVariant(variant, productInfoNode);
        shareUrl.searchParams.set("variant", variantId);
        if (shade) {
          shareUrl.searchParams.set("shade", shade);
        }
        shareButton.updateUrl(
          shareUrl.toString()
        );
      }

      toggleAddButton(disable = true, text, modifyClass = true) {
        const productForm = document.getElementById(
          `product-form-${this.dataset.section}`
        );
        if (!productForm) return;
        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText = productForm.querySelector('[name="add"] > span');
        if (!addButton) return;

        if (disable) {
          addButton.setAttribute("disabled", "disabled");
          if (text) addButtonText.textContent = this.decoded(text);
        } else {
          addButton.removeAttribute("disabled");
          addButtonText.textContent = this.decoded(
            FoxTheme.variantStrings.addToCart
          );
        }

        if (!modifyClass) return;
      }

      decoded(text) {
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = text;
        const decoded = tempDiv.textContent;
        return decoded;
      }

      setUnavailable() {
        this.toggleAddButton(true, FoxTheme.variantStrings.unavailable);
        const price = document.getElementById(`price-${this.dataset.section}`);
        const inventory = document.getElementById(
          `Inventory-${this.dataset.section}`
        );
        const sku = document.getElementById(`Sku-${this.dataset.section}`);

        if (price) price.classList.add("hidden");
        if (inventory) inventory.classList.add("hidden");
        if (sku) sku.classList.add("hidden");
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.setQuantityBoundries();
        if (!this.hasAttribute("data-original-section")) {
          this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      setQuantityBoundries() {
        FoxTheme.pubsub.publish(
          FoxTheme.pubsub.PUB_SUB_EVENTS.quantityBoundries,
          {
            data: {
              sectionId: this.sectionId,
              productId: this.productId,
            },
          }
        );
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.productIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector(".quantity__rules-cart")?.classList.add(
          "btn--loading"
        );

        fetch(
          `${this.getAttribute(
            "data-url"
          )}?variant=${currentVariantId}&section_id=${this.sectionId}`
        )
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            this.updateQuantityRules(
              this.sectionId,
              this.productId,
              parsedHTML
            );
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.querySelector(".quantity__rules-cart")?.classList.remove(
              "btn--loading"
            );
          });
      }

      updateQuantityRules(sectionId, productId, parsedHTML) {
        if (!this.quantityInput) return;

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityRules, {
          data: {
            sectionId,
            productId,
            parsedHTML,
          },
        });

        this.setQuantityBoundries();
      }
    }
  );
}
